import React, { useState, useRef, useEffect, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useNavigate } from "react-router-dom";
import { COMPANY } from "../constants/company";

// Google Maps Geocoding key. Restrict this in Google Cloud Console to
// the production domain + localhost — keys in client code are visible.
const GOOGLE_GEOCODE_KEY = "AIzaSyAA5mCXU8WrBOym-1rZvzADu4YvZWhAja8";

// Presentational only — defined outside the component so it never remounts
// the form inputs (which would drop focus while typing).
const SectionHeader = ({ step, icon, title, subtitle, done }) => (
  <div className="flex items-start gap-3.5 mb-5">
    <div
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg transition-all duration-300 ${
        done
          ? "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-200/70"
          : "bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-200/70"
      }`}
    >
      <i className={`fa ${done ? "fa-check" : icon} text-base`}></i>
    </div>
    <div className="pt-0.5 flex-1">
      <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-indigo-500">
        Step {step}
      </span>
      <p className="text-base font-extrabold text-slate-900 leading-tight">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// Horizontal progress stepper — reflects live completion across the flow.
const Stepper = ({ steps }) => (
  <div className="flex items-start">
    {steps.map((s, i) => (
      <React.Fragment key={s.label}>
        <div className="flex flex-col items-center">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ring-2 transition-all duration-300 ${
              s.done
                ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white ring-emerald-200"
                : s.active
                ? "bg-white text-indigo-700 ring-indigo-400 shadow-md shadow-indigo-200"
                : "bg-slate-100 text-slate-400 ring-slate-200"
            }`}
          >
            {s.done ? <i className="fa fa-check"></i> : i + 1}
          </div>
          <span
            className={`mt-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-center ${
              s.done || s.active ? "text-slate-700" : "text-slate-400"
            }`}
          >
            {s.label}
          </span>
        </div>
        {i < steps.length - 1 && (
          <div className="mt-5 flex-1 mx-2 h-1 overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500 ${
                s.done ? "w-full" : "w-0"
              }`}
            />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

export default function NewKyc() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const sigPadRef = useRef(null);
  const [signature, setSignature] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [panCard, setPanCard] = useState("");
  const [panCardImage, setPanCardImage] = useState(null);
  const [panCardImagePreview, setPanCardImagePreview] = useState(null);
  const [panCardImageError, setPanCardImageError] = useState(null);

  // Geolocation — captured for the regulatory audit trail.
  // status: idle → asking → granted | denied | unavailable | error
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [geoStatus, setGeoStatus] = useState("idle");
  const [geoError, setGeoError] = useState("");

  const getAddress = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_GEOCODE_KEY}`
      );
      const data = await res.json();

      if (data.results && data.results.length > 0) {
        const result = data.results[0];

        let area = "";
        let city = "";
        let state = "";
        let country = "";
        let pincode = "";

        result.address_components.forEach((comp) => {
          const types = comp.types;
          if (types.includes("sublocality") || types.includes("neighborhood")) area = comp.long_name;
          if (types.includes("locality")) city = comp.long_name;
          if (types.includes("administrative_area_level_1")) state = comp.long_name;
          if (types.includes("country")) country = comp.long_name;
          if (types.includes("postal_code")) pincode = comp.long_name;
        });

        const parts = [area, city, state, country].filter(Boolean).join(", ");
        setAddress(pincode ? `${parts} - ${pincode}` : parts);
      }
    } catch (err) {
      console.error("Geocode Error:", err);
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeoStatus("unavailable");
      setGeoError("Your browser does not support location services.");
      return;
    }

    setGeoStatus("asking");
    setGeoError("");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setCoords({ lat, lng });
        setGeoStatus("granted");
        getAddress(lat, lng);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGeoStatus("denied");
          setGeoError(
            "Location access was blocked. Please allow location in your browser settings to continue."
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGeoStatus("error");
          setGeoError("We couldn't determine your location. Please try again.");
        } else if (err.code === err.TIMEOUT) {
          setGeoStatus("error");
          setGeoError("Location request timed out. Please try again.");
        } else {
          setGeoStatus("error");
          setGeoError("Something went wrong while fetching your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, [getAddress]);

  // Ask on mount. The popup handles denied/error states with a retry button.
  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  const clearSignature = () => {
    sigPadRef.current?.clear();
    setSignature(null);
    setIsSaved(false);
  };

  const saveSignature = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const data = sigPadRef.current.toDataURL();
      setSignature(data);
      setIsSaved(true);
    }
  };

  const handlePanCardImageChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
      setPanCardImageError("Only PNG, JPG or PDF files are allowed.");
      setPanCardImage(null);
      setPanCardImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setPanCardImageError("File too large (max 5MB)");
      setPanCardImage(null);
      setPanCardImagePreview(null);
      return;
    }

    setPanCardImageError(null);
    setPanCardImage(file);

    const reader = new FileReader();
    reader.onload = () => {
      setPanCardImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!fullName || !email || !mobile || !panCard || !panCardImage) {
      alert("Fill all required details");
      return;
    }

    if (!signature) {
      alert("Signature is required");
      return;
    }

    if (!panCardImagePreview) {
      alert("PAN card image is required");
      return;
    }

    setLoading(true);

    // Backend's /submitandpay accepts multipart (same uploadFields as /submit),
    // so we send the PAN file as a real file rather than base64 — matches the
    // controller's req.files.panDoc path.
    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("mobile", mobile);
    formData.append("pan", panCard);
    formData.append("signatureBase64", signature);
    formData.append("panDoc", panCardImage);

    // Location is optional at the backend (falls back to IP), but include it
    // when the user has granted permission — it goes into the audit trail.
    if (coords) {
      formData.append(
        "location",
        address || `Lat ${coords.lat}, Lng ${coords.lng}`
      );
      formData.append("lat", String(coords.lat));
      formData.append("lng", String(coords.lng));
    }

    try {
      const res = await fetch(
        `${COMPANY.API_BASE_URL}/api/submitandpay`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await res.json();

      if (res.ok) {
        sessionStorage.setItem(
          "submissionData",
          JSON.stringify({ fullName, email, mobile, panCard })
        );
        navigate("/thank-you");
      } else {
        alert(data.message || "Failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all outline-none";
  const labelCls =
    "text-[11px] font-bold uppercase text-slate-500 mb-1.5 block tracking-wider";
  const iconCls =
    "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors peer-focus:text-indigo-500";

  // Live completion state — drives the sidebar stepper + progress bar.
  const step1Done = !!(fullName && email && mobile && panCard);
  const step2Done = !!panCardImage;
  const step3Done = !!signature;
  const completedCount = [step1Done, step2Done, step3Done].filter(Boolean).length;
  const progress = Math.round((completedCount / 3) * 100);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 flex items-center justify-center p-4 md:p-8">
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"
      />

      {/* Page-scoped animations for the background */}
      <style>{`
        @keyframes kycDrift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(18px, -22px) scale(1.06); }
        }
        @keyframes kycDrift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(-22px, 18px) scale(1.08); }
        }
        .kyc-blob   { animation: kycDrift 16s ease-in-out infinite; }
        .kyc-blob-2 { animation: kycDrift2 20s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .kyc-blob, .kyc-blob-2 { animation: none; }
        }
      `}</style>

      {/* Soft aurora wash — layered radial glows for an elegant, premium base */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(45% 55% at 12% 18%, rgba(129,140,248,0.30), transparent 70%),
            radial-gradient(42% 52% at 88% 12%, rgba(236,72,153,0.22), transparent 70%),
            radial-gradient(50% 60% at 82% 88%, rgba(168,85,247,0.26), transparent 70%),
            radial-gradient(45% 55% at 8% 92%, rgba(56,189,248,0.20), transparent 70%)
          `,
        }}
      />

      {/* Very subtle dot-grid for texture, faded toward the edges */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(99,102,241,0.10) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 78%)",
        }}
      />

      {/* Gently drifting ambient blobs (soft, low opacity) */}
      <div className="kyc-blob pointer-events-none absolute -top-28 -left-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
      <div className="kyc-blob-2 pointer-events-none absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full bg-fuchsia-300/25 blur-3xl" />
      <div className="kyc-blob pointer-events-none absolute top-1/3 right-1/4 h-80 w-80 rounded-full bg-violet-300/20 blur-3xl" />

      {/* Location permission popup — appears when the browser blocks geolocation
          or the lookup fails. Backed by the same retry handler used on mount. */}
      {(geoStatus === "denied" || geoStatus === "error" || geoStatus === "unavailable") && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur max-w-md w-full rounded-3xl p-7 shadow-2xl border border-white/60">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-lg shadow-rose-200">
                <i className="fa fa-map-marker-alt text-lg"></i>
              </div>
              <h4 className="text-lg font-extrabold text-slate-900">
                Location Access Required
              </h4>
            </div>
            <p className="text-sm text-slate-700 mb-3">{geoError}</p>
            <p className="text-xs text-slate-500 mb-5 leading-relaxed">
              We capture your location only to record where the agreement was
              signed — it's part of the regulatory audit trail. Open your
              browser's site settings to allow location for this site, then tap
              Retry.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setGeoStatus("skipped")}
                className="px-4 py-2 text-sm font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Continue without location
              </button>
              <button
                type="button"
                onClick={requestLocation}
                className="px-5 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-200 hover:shadow-xl"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full max-w-2xl space-y-5">
        {/* ===================== Hero card ===================== */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-900 via-purple-700 to-fuchsia-700 p-7 md:p-9 text-white shadow-md shadow-indigo-300/25">
          <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute right-24 -bottom-12 h-32 w-32 rounded-full bg-white/10" />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur ring-1 ring-white/30">
                <i className="fa fa-shield-halved text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-extrabold leading-tight">
                  {COMPANY.SHORT_NAME.trim()}
                </h2>
                <p className="text-indigo-200 text-[10px] font-semibold uppercase tracking-[0.25em]">
                  Research &amp; Advisory
                </p>
              </div>
            </div>

            {/* Location status pill */}
            <div className="text-xs">
              {geoStatus === "asking" && (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/20">
                  <i className="fa fa-spinner fa-spin"></i> Locating…
                </span>
              )}
              {geoStatus === "granted" && (
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/20 px-3 py-1.5 ring-1 ring-emerald-200/40">
                  <i className="fa fa-location-dot text-emerald-200"></i>
                  <span className="truncate max-w-[180px]">
                    {address || "Location captured"}
                  </span>
                </span>
              )}
              {geoStatus === "skipped" && (
                <button
                  type="button"
                  onClick={requestLocation}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400/20 px-3 py-1.5 ring-1 ring-amber-200/40"
                >
                  <i className="fa fa-triangle-exclamation text-amber-200"></i> Enable location
                </button>
              )}
            </div>
          </div>

          <h1 className="relative mt-6 text-2xl md:text-3xl font-extrabold leading-snug">
            Complete your KYC verification
          </h1>
          <p className="relative mt-1 text-sm text-indigo-100">
            It only takes a minute — your details are encrypted and stored securely.
          </p>

          <div className="relative mt-5 flex flex-wrap gap-2">
            {[
              { icon: "fa-circle-check", label: "SEBI-Registered RA" },
              { icon: "fa-lock", label: "Encrypted Submission" },
              { icon: "fa-headset", label: "Premium Support" },
            ].map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium ring-1 ring-white/20"
              >
                <i className={`fa ${b.icon} text-emerald-300`}></i>
                {b.label}
              </span>
            ))}
          </div>
        </div>

        {/* ===================== Progress stepper card ===================== */}
        <div className="rounded-3xl bg-white/95 backdrop-blur-xl p-5 md:p-6 shadow-sm ring-1 ring-slate-200/70 border border-white/60">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Your progress
            </span>
            <span className="text-xs font-extrabold text-indigo-600">
              {progress}% complete
            </span>
          </div>
          <Stepper
            steps={[
              { label: "Personal", done: step1Done, active: !step1Done },
              { label: "Document", done: step2Done, active: step1Done && !step2Done },
              { label: "Signature", done: step3Done, active: step1Done && step2Done && !step3Done },
            ]}
          />
        </div>

        <form className="space-y-5">
          {/* ===== Section 1 — Personal Details ===== */}
          <section className="rounded-3xl bg-white/95 backdrop-blur-xl p-6 md:p-8 shadow-sm ring-1 ring-slate-200/70 border border-white/60">
            <SectionHeader
              step={1}
              icon="fa-user"
              title="Personal Details"
              subtitle="Tell us who you are."
              done={step1Done}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Full Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    className={`peer ${inputCls}`}
                  />
                  <i className={`fa fa-user ${iconCls}`}></i>
                </div>
              </div>

              <div>
                <label className={labelCls}>Email Address</label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={`peer ${inputCls}`}
                  />
                  <i className={`fa fa-envelope ${iconCls}`}></i>
                </div>
              </div>

              <div>
                <label className={labelCls}>Mobile Number</label>
                <div className="relative">
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="+91 00000 00000"
                    className={`peer ${inputCls}`}
                  />
                  <i className={`fa fa-phone ${iconCls}`}></i>
                </div>
              </div>

              <div>
                <label className={labelCls}>PAN Card Number</label>
                <div className="relative">
                  <input
                    type="text"
                    value={panCard}
                    onChange={(e) => setPanCard(e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F"
                    className={`peer ${inputCls} tracking-widest uppercase`}
                  />
                  <i className={`fa fa-id-card ${iconCls}`}></i>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Section 2 — PAN Document ===== */}
          <section className="rounded-3xl bg-white/95 backdrop-blur-xl p-6 md:p-8 shadow-sm ring-1 ring-slate-200/70 border border-white/60">
            <SectionHeader
              step={2}
              icon="fa-file-arrow-up"
              title="PAN Card Document"
              subtitle="Upload a clear image or PDF (max 5MB)."
              done={step2Done}
            />

            <label
              htmlFor="panDocInput"
              className="group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 px-6 py-8 text-center transition-all hover:border-indigo-400 hover:bg-indigo-50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200 transition-transform group-hover:scale-105">
                <i className="fa fa-cloud-arrow-up text-lg"></i>
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {panCardImage ? "Change file" : "Click to upload PAN card"}
              </p>
              <p className="text-xs text-slate-400 mt-1">PNG, JPG or PDF</p>
              <input
                id="panDocInput"
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                onChange={handlePanCardImageChange}
                className="hidden"
              />
            </label>

            {panCardImage && (
              <p className="mt-2 text-xs text-slate-500 flex items-center gap-2">
                <i className="fa fa-paperclip text-indigo-500"></i>
                {panCardImage.name}
              </p>
            )}

            {panCardImagePreview && panCardImage?.type.startsWith("image/") && (
              <div className="mt-3">
                <p className="text-[11px] text-slate-500 mb-2">Preview</p>
                <img
                  src={panCardImagePreview}
                  alt="PAN card preview"
                  className="max-h-52 w-full rounded-2xl border border-slate-200 object-contain bg-white"
                />
              </div>
            )}

            {panCardImage && panCardImage.type === "application/pdf" && (
              <p className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                <i className="fa fa-file-pdf text-rose-500"></i>
                PDF selected. Preview is not available for PDFs.
              </p>
            )}

            {panCardImageError && (
              <p className="text-xs text-rose-500 mt-2 flex items-center gap-2">
                <i className="fa fa-circle-exclamation"></i>
                {panCardImageError}
              </p>
            )}
          </section>

          {/* ===== Section 3 — E-Signature ===== */}
          <section className="rounded-3xl bg-white/95 backdrop-blur-xl p-6 md:p-8 shadow-sm ring-1 ring-slate-200/70 border border-white/60">
            <SectionHeader
              step={3}
              icon="fa-signature"
              title="E-Signature"
              subtitle="Sign inside the box below."
              done={step3Done}
            />

            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase text-slate-500 tracking-wider">
                Draw your signature
              </span>
              {isSaved && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-[11px] font-bold ring-1 ring-emerald-200">
                  <i className="fa fa-check"></i> Captured
                </span>
              )}
            </div>

            <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-indigo-50/40 p-3">
              <SignatureCanvas
                penColor="#312e81"
                canvasProps={{
                  className: "w-full h-40 rounded-xl bg-white shadow-inner",
                }}
                onEnd={() =>
                  setSignature(sigPadRef.current?.toDataURL() ?? null)
                }
                ref={sigPadRef}
              />
            </div>

            <div className="flex gap-3 mt-3">
              <button
                type="button"
                onClick={clearSignature}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <i className="fa fa-eraser"></i> Clear
              </button>

              <button
                type="button"
                onClick={saveSignature}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-xl text-white transition-all ${
                  isSaved
                    ? "bg-emerald-600"
                    : "bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg shadow-indigo-200 hover:shadow-xl"
                }`}
              >
                <i className={`fa ${isSaved ? "fa-check" : "fa-floppy-disk"}`}></i>
                {isSaved ? "Signature Saved" : "Save Signature"}
              </button>
            </div>

            {!signature && (
              <p className="text-xs text-rose-500 mt-2 flex items-center gap-2">
                <i className="fa fa-circle-exclamation"></i> Signature is required
              </p>
            )}
          </section>

          {/* ===== Submit card ===== */}
          <div className="rounded-3xl bg-white/95 backdrop-blur-xl p-6 shadow-sm ring-1 ring-slate-200/70 border border-white/60">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!fullName || !email || !mobile || !panCard || !panCardImage || !signature || loading}
              className="group w-full flex items-center justify-center gap-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg shadow-purple-200 transition-all hover:shadow-2xl hover:shadow-purple-300 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  Submit KYC
                  <i className="fa fa-arrow-right transition-transform group-hover:translate-x-1"></i>
                </>
              )}
            </button>

            <p className="mt-3 text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
              <i className="fa fa-lock"></i>
              Safe &amp; secure KYC submission with encrypted verification
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
